import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
// 06/10/2026 : barra lateral « inset » de la demo factures (shadcn sidebar-07): la barra en gris claro y la
// página como un panel blanco levantado. Iconos Phosphor duotone.
import {
  BuildingsIcon, CalendarDotsIcon, ChartBarIcon, CaretUpDownIcon, CheckIcon, DotsThreeIcon, DotsThreeVerticalIcon,
  FlowArrowIcon, GearSixIcon, HouseIcon, MapPinIcon, MegaphoneIcon, MoonIcon, PlugsConnectedIcon, RobotIcon,
  SidebarSimpleIcon, SignOutIcon, SunIcon, TranslateIcon, TrayIcon, UsersThreeIcon, WifiSlashIcon, type IconProps,
} from "@phosphor-icons/react";
import { useEffect, useState, type ComponentType } from "react";
import { useIntl } from "react-intl";
import { escribir } from "@responia/api/cliente";
import type { Accion } from "@responia/api/permisos";
import { Button } from "@responia/ui/components/button";
import { Marca } from "@responia/ui/components/marca";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@responia/ui/components/dropdown-menu";
import { Separator } from "@responia/ui/components/separator";
import { Sheet, SheetContent, SheetTitle } from "@responia/ui/components/sheet";
import { cn } from "@responia/ui/lib/utils";
import { useLectura } from "@/datos";
import { IDIOMAS, NOMBRE_IDIOMA, type Clave, type Idioma } from "@/i18n";
import { Ltr } from "@/ltr";
import { useT } from "@/useT";
import { usePreferencias } from "@/preferencias";
import { useSede } from "@/sede";
import { useSesion } from "@/sesion";
import { Buscador } from "./Buscador";

type Entrada = { clave: Clave; corta?: Clave; ruta: string; Icono: ComponentType<IconProps>; accion?: Accion };

// Cada grupo lleva su rótulo (null: sin rótulo) para que la barra se lea de un vistazo.
const ROTULOS: (Clave | null)[] = ["nav.grupo.dia", "nav.grupo.asistente", null];

const GRUPOS: Entrada[][] = [
  [
    { clave: "nav.hoy", ruta: "/", Icono: HouseIcon },
    { clave: "nav.bandeja", corta: "nav.bandejaCorta", ruta: "/bandeja", Icono: TrayIcon },
    { clave: "nav.agenda", ruta: "/agenda", Icono: CalendarDotsIcon },
    { clave: "nav.contactos", ruta: "/contactos", Icono: UsersThreeIcon },
    { clave: "nav.campanas", ruta: "/campanas", Icono: MegaphoneIcon },
    { clave: "nav.analisis", ruta: "/analisis", Icono: ChartBarIcon },
  ],
  [
    { clave: "nav.asistente", ruta: "/asistente", Icono: RobotIcon },
    { clave: "nav.automatizaciones", ruta: "/automatizaciones", Icono: FlowArrowIcon },
    { clave: "nav.integraciones", ruta: "/integraciones", Icono: PlugsConnectedIcon },
  ],
  [{ clave: "nav.ajustes", ruta: "/ajustes", Icono: GearSixIcon }],
];

const MOVIL: Clave[] = ["nav.hoy", "nav.bandeja", "nav.agenda", "nav.contactos"];

// Le pictogramme porte déjà son fond (rond azul tinta), identique au favicon dans les deux thèmes.

// Los contadores de la barra: quién espera en la Bandeja, las citas de hoy en la Agenda.
type Cuentas = Partial<Record<string, { n: number; clave: Clave }>>;

function Enlace({ e, onClick, cuenta }: { e: Entrada; onClick?: () => void; cuenta?: { n: number; clave: Clave } }) {
  const t = useT();
  const ruta = useRouterState({ select: (s) => s.location.pathname.replace(/^\/v2/, "") || "/" });
  const activo = ruta === e.ruta;
  // Como SidebarMenuButton de shadcn: 32 px de alto, la entrada activa en fondo sidebar-accent.
  return (
    <Link
      to={e.ruta}
      onClick={onClick}
      aria-current={activo ? "page" : undefined}
      className={cn(
        "flex h-8 items-center gap-x-2 rounded-md px-2 text-sm transition-colors",
        "outline-ring focus-visible:outline-2 focus-visible:outline-offset-2",
        activo ? "bg-sidebar-accent font-medium text-foreground [&>svg]:text-primary" : "text-sidebar-foreground hover:bg-sidebar-accent/70",
      )}
    >
      <e.Icono weight="duotone" className="size-4 shrink-0" aria-hidden />
      {t(e.clave)}
      {cuenta && cuenta.n > 0 && (
        <span data-cuenta className="ms-auto min-w-5 rounded-md px-1 text-center text-xs font-medium tabular-nums text-muted-foreground">
          <span aria-hidden>{cuenta.n}</span><span className="sr-only">{t(cuenta.clave, { n: cuenta.n })}</span>
        </span>
      )}
    </Link>
  );
}

// El selector de cuenta y el de sede usan el disparador de « workspace » de Tremor: marco de 1 px, sombra xs.
const DISPARADOR = "flex h-auto w-full items-center gap-x-2.5 rounded-lg border bg-card p-2 text-start text-sm font-normal shadow-xs transition-colors hover:bg-muted/60";

function SelectorCliente() {
  const t = useT();
  const { me } = useSesion();
  const qc = useQueryClient();
  if (me.clientes.length < 2) return null;
  const cambiar = async (id: string) => {
    await escribir("/sesion/cliente", { cliente: id });
    await qc.invalidateQueries();
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn(DISPARADOR, "outline-ring focus-visible:outline-2 focus-visible:outline-offset-2")} aria-label={t("menu.cambiarCuenta")}>
          <span className="grid size-8 shrink-0 place-items-center rounded-md bg-marca-fond text-primary" aria-hidden>
            <BuildingsIcon weight="duotone" className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-foreground">{me.cliente.nombre}</span>
            <span className="block truncate text-xs text-muted-foreground">{t(`rol.${me.rol}`)}</span>
          </span>
          <CaretUpDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{t("menu.cuentas")}</DropdownMenuLabel>
        {me.clientes.map((c) => (
          <DropdownMenuItem key={c.id} onSelect={() => cambiar(c.id)}>
            <span className="truncate">{c.nombre}</span>
            {c.id === me.cliente.id && <CheckIcon className="ms-auto size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Solo con dos sedes visibles o más. La elección va a la URL (?sede=) y se queda al navegar.
function SelectorSede({ onElegir }: { onElegir?: () => void }) {
  const t = useT();
  const { sedes, actual, todas, multi } = useSede();
  const navegar = useNavigate();
  if (!multi) return null;
  const elegir = (sede?: string) => {
    navegar({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, sede }) });
    onElegir?.();
  };
  const general = t(todas ? "sedes.selector.todas" : "sedes.selector.tuyas");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-selector-sede className={cn(DISPARADOR, "py-1.5 outline-ring focus-visible:outline-2 focus-visible:outline-offset-2")} aria-label={t("sedes.selector.cambiar", { sede: actual?.nombre ?? general })}>
          <MapPinIcon weight="duotone" className="ms-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-medium text-foreground">{actual?.nombre ?? general}</span>
          <CaretUpDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{t("sedes.selector.titulo")}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => elegir(undefined)}>
          <span className="truncate">{general}</span>
          {!actual && <CheckIcon className="ms-auto size-4" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {sedes.map((s) => (
          <DropdownMenuItem key={s.id} onSelect={() => elegir(s.id)}>
            <span className="truncate">{s.nombre}</span>
            {s.id === actual?.id && <CheckIcon className="ms-auto size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Dos letras, como el perfil de la plantilla Tremor: « Camila Pante » da « CP ».
const iniciales = (nombre: string) =>
  nombre.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

function MenuUsuario() {
  const t = useT();
  const { me } = useSesion();
  const { idioma, setIdioma, tema, setTema } = usePreferencias();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const salir = async () => {
    try { await escribir("/salir"); } finally {
      qc.clear();
      navegar({ to: "/acceso" });
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="group h-auto w-full justify-between gap-3 p-2 hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent">
          <span className="flex min-w-0 items-center gap-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-neutral-900 text-xs font-semibold text-white dark:bg-white dark:text-neutral-900" aria-hidden>
              {iniciales(me.usuario.nombre || me.usuario.email)}
            </span>
            <span className="min-w-0 truncate text-sm font-medium text-foreground">{me.usuario.nombre || <Ltr>{me.usuario.email}</Ltr>}</span>
          </span>
          <DotsThreeVerticalIcon weight="bold" className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm"><Ltr>{me.usuario.email}</Ltr></p>
          <p className="text-xs font-medium text-muted-foreground">{t(`rol.${me.rol}`)}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <TranslateIcon className="size-3.5" /> {t("menu.idioma")}
        </DropdownMenuLabel>
        {IDIOMAS.map((l: Idioma) => (
          <DropdownMenuItem key={l} onSelect={() => setIdioma(l)}>
            {NOMBRE_IDIOMA[l]}
            {l === idioma && <CheckIcon className="ms-auto size-4" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{t("menu.tema")}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => setTema("claro")}>
          <SunIcon className="size-4" /> {t("menu.tema.claro")}
          {tema === "claro" && <CheckIcon className="ms-auto size-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setTema("oscuro")}>
          <MoonIcon className="size-4" /> {t("menu.tema.oscuro")}
          {tema === "oscuro" && <CheckIcon className="ms-auto size-4" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={salir}>
          <SignOutIcon className="size-4" /> {t("menu.cerrarSesion")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// La tarjeta « Tu asistente contesta » al pie de la barra: el estado y los canales conectados.
function TarjetaAsistente() {
  const t = useT();
  const intl = useIntl();
  const { me } = useSesion();
  const enLinea = me.asistente.en_linea;
  const nombres = { telefono: "hoy.canal.telefono", whatsapp: "hoy.canal.whatsapp", web: "hoy.canal.web" } as const;
  return (
    <Link to="/asistente" data-tarjeta-asistente
      className="block rounded-lg border bg-card px-3 py-2.5 shadow-xs transition-colors outline-ring hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-offset-2">
      <span className="flex items-center gap-2 text-[13px] font-medium text-foreground">
        <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", enLinea ? "bg-prouve shadow-[0_0_0_3px_color-mix(in_oklab,var(--prouve)_20%,transparent)]" : "bg-muted-foreground")} />
        {t(enLinea ? "shell.asistente.contesta" : "estado.fueraDeLinea")}
      </span>
      {me.asistente.canales.length > 0 && (
        <span className="mt-0.5 block ps-3.5 text-xs text-muted-foreground">
          {intl.formatList(me.asistente.canales.map((c) => t(nombres[c])), { type: "conjunction" })}
        </span>
      )}
    </Link>
  );
}

// La barra lateral se pliega (botón de la barra superior o ⌘B / Ctrl B, como la sidebar de shadcn).
// La elección es una comodidad de este navegador: si el almacenamiento falla, la barra queda abierta.
const CLAVE_PLEGADA = "responia.barraPlegada";
function usePlegada() {
  const [plegada, setPlegada] = useState(() => {
    try { return localStorage.getItem(CLAVE_PLEGADA) === "1"; } catch { return false; }
  });
  const cambiar = () => setPlegada((v) => {
    try { localStorage.setItem(CLAVE_PLEGADA, v ? "0" : "1"); } catch { /* sin almacenamiento */ }
    return !v;
  });
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "b" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); cambiar(); }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);
  return [plegada, cambiar] as const;
}

export function Shell() {
  const t = useT();
  const { me, directo } = useSesion();
  const [mas, setMas] = useState(false);
  const [plegada, plegar] = usePlegada();
  const sede = useSede();
  const { multi } = sede;
  // Los contadores leen lo que Hoy ya trajo (misma clave) sin pedirlo de nuevo: /hoy lee la agenda
  // de Google, y pedirlo en cada pantalla multiplicaría esas lecturas. Se ponen al día con Hoy.
  const { data: hoy } = useLectura("/hoy", sede.params, false);
  const cuentas: Cuentas = hoy ? {
    "/bandeja": { n: hoy.n_pendientes, clave: "nav.cuenta.bandeja" },
    "/agenda": { n: hoy.citas_hoy, clave: "nav.cuenta.agenda" },
  } : {};
  const todas = GRUPOS.flat();
  const ruta = useRouterState({ select: (s) => s.location.pathname.replace(/^\/v2/, "") || "/" });
  const pleineLargeur = /^\/bandeja/.test(ruta);
  // El nombre de la pantalla en la barra superior: la entrada cuya ruta empieza la ruta actual.
  const pantalla = [...todas].sort((a, b) => b.ruta.length - a.ruta.length)
    .find((e) => e.ruta === "/" ? ruta === "/" : ruta === e.ruta || ruta.startsWith(`${e.ruta}/`));

  return (
    <div className="min-h-dvh bg-background min-[900px]:bg-sidebar">
      <aside className={cn("fixed inset-y-0 start-0 z-30 hidden w-64 flex-col gap-y-4 overflow-y-auto bg-sidebar p-3", !plegada && "min-[900px]:flex")}>
        <div className="flex items-center gap-x-2.5 p-1.5">
          <span className="min-w-0">
            <Marca className="h-7 w-auto text-foreground" titulo={t("app.nombre")} />
            <span className="mt-1 block truncate text-xs text-muted-foreground">{me.cliente.nombre}</span>
          </span>
        </div>
        <Buscador destinos={todas.map((e) => ({ etiqueta: t(e.clave), ruta: e.ruta, Icono: e.Icono }))} />
        {(me.clientes.length > 1 || multi) && (
          <div className="flex flex-col gap-2">
            <SelectorCliente />
            <SelectorSede />
          </div>
        )}
        <nav className="flex flex-1 flex-col gap-y-4" aria-label={t("nav.principal")}>
          {GRUPOS.map((g, i) => (
            <div key={i}>
              {ROTULOS[i] && <p className="flex h-8 items-center px-2 text-xs font-medium text-muted-foreground">{t(ROTULOS[i])}</p>}
              <ul role="list" className="flex flex-col gap-y-0.5">
                {g.map((e) => <li key={e.ruta}><Enlace e={e} cuenta={cuentas[e.ruta]} /></li>)}
              </ul>
            </div>
          ))}
        </nav>
        <div className="flex flex-col gap-3">
          <TarjetaAsistente />
          <MenuUsuario />
        </div>
      </aside>

      {directo === "reconectando" && (
        <div role="status" className={cn("fixed inset-x-0 top-0 z-40 flex items-center justify-center gap-2 border-b border-trace/20 bg-trace-fond py-1.5 text-xs font-medium text-trace min-[900px]:start-64", plegada && "min-[900px]:start-0")}>
          <WifiSlashIcon className="size-3.5" aria-hidden /> {t("estado.reconectando")}
        </div>
      )}

      {/* La bandeja a tres columnas ocupa todo el ancho; las demás vistas guardan una línea legible. */}
      {/* El panel blanco levantado (SidebarInset variant="inset"): margen de 8 px, radio xl, sombra suave. */}
      <div className={cn("min-[900px]:py-2 min-[900px]:pe-2", plegada ? "min-[900px]:ps-2" : "min-[900px]:ms-64")}>
        <main className="bg-background pb-24 min-[900px]:min-h-[calc(100dvh-1rem)] min-[900px]:rounded-xl min-[900px]:pb-10 min-[900px]:shadow-sm min-[900px]:ring-1 min-[900px]:ring-foreground/5">
          {/* Barra superior de la demo factures: plegar la barra lateral y el nombre de la pantalla. */}
          <div className="sticky top-0 z-20 hidden h-12 items-center gap-2 rounded-t-xl border-b bg-background/95 px-4 backdrop-blur min-[900px]:flex lg:px-6">
            <Button variant="ghost" size="icon-sm" onClick={plegar} aria-expanded={!plegada}
              aria-label={t(plegada ? "shell.barra.mostrar" : "shell.barra.ocultar")} title={`${t(plegada ? "shell.barra.mostrar" : "shell.barra.ocultar")} (${/Mac/.test(navigator.platform) ? "⌘B" : "Ctrl B"})`}>
              <SidebarSimpleIcon aria-hidden />
            </Button>
            <Separator orientation="vertical" className="mx-1 data-[orientation=vertical]:h-4" />
            {pantalla && <p className="truncate text-sm font-medium text-foreground">{t(pantalla.clave)}</p>}
          </div>
          <div className={cn("px-4 pt-6", pleineLargeur ? "min-[900px]:px-6" : "sm:px-6 min-[900px]:px-10")}>
            <div className={cn(!pleineLargeur && "mx-auto max-w-7xl")}><Outlet /></div>
          </div>
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-card pb-[env(safe-area-inset-bottom)] min-[900px]:hidden" aria-label={t("nav.principal")}>
        {todas.filter((e) => MOVIL.includes(e.clave)).map((e) => {
          const activo = ruta === e.ruta;
          return (
            <Link key={e.ruta} to={e.ruta} aria-current={activo ? "page" : undefined}
              className={cn("flex min-w-0 flex-1 flex-col items-center gap-1 py-2 text-[11px] font-medium", activo ? "text-primary" : "text-muted-foreground")}>
              <e.Icono weight={activo ? "fill" : "duotone"} className="size-5" aria-hidden />
              <span className="max-w-full truncate px-1">{t(e.corta ?? e.clave)}</span>
            </Link>
          );
        })}
        <button type="button" onClick={() => setMas(true)} className="flex min-w-0 flex-1 flex-col items-center gap-1 py-2 text-[11px] font-medium text-muted-foreground">
          <DotsThreeIcon weight="bold" className="size-5" aria-hidden />
          {t("nav.mas")}
        </button>
      </nav>

      <Sheet open={mas} onOpenChange={setMas}>
        <SheetContent side="bottom" className="gap-0 rounded-t-lg p-4">
          <SheetTitle className="sr-only">{t("nav.mas")}</SheetTitle>
          <div className="flex flex-col gap-2 pt-6">
            <SelectorCliente />
            <SelectorSede onElegir={() => setMas(false)} />
          </div>
          <ul role="list" className="flex flex-col gap-y-0.5 py-3">
            {todas.filter((e) => !MOVIL.includes(e.clave)).map((e) => <li key={e.ruta}><Enlace e={e} onClick={() => setMas(false)} /></li>)}
          </ul>
          <div className="border-t border-border pt-3">
            <MenuUsuario />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
